import {FC, useEffect, useState} from 'react'
import {i18n} from '../../../../translate/i18n'
import styles from './EditCategoria.module.css'
import {Input} from '../../../Input/Input'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {POST, PUT} from '../../../../services/api'
import {removeEspacosEmBranco} from '../../../../utils/formater'
import uuid from 'react-uuid'

type Props = {
  onCloseAction: () => void
  reloadAction: () => Promise<void>
  setLoading: (value: boolean) => void
  category: CategoryType
  categories: CategoryType[]
  integration: 'totvsFashionVarejo' | 'totvsFood' | 'none'
}

type CategoryType = {
  id: string
  name: string
  code: string
  order: string
  status: boolean
  img: File | null | string
  parent: string
}

type ParentType = {
  value: string
  label: string
}

function getDescendants(categories: CategoryType[], categoryCode: string): string[] {
  const directChildren = categories.filter((cat) => cat.parent === categoryCode)
  let allDescendants: string[] = []
  for (const child of directChildren) {
    allDescendants.push(child.code)
    allDescendants = allDescendants.concat(getDescendants(categories, child.code))
  }
  return allDescendants
}

const EditCategoriaModal: FC<Props> = ({
  onCloseAction,
  reloadAction,
  setLoading,
  category,
  categories,
  integration,
}) => {
  const navigate = useNavigate()

  const [name, setName] = useState<string>(category.name)
  const [code, setCode] = useState<string>(category.code)
  const [order, setOrder] = useState<string>(category.order)
  const [status, setStatus] = useState<boolean>(category.status)
  const [firstStateImage, setFirstStateImage] = useState<File | null | string>(null)
  const [image, setImage] = useState<File | null | string>(null)
  const [parent, setParent] = useState<ParentType>()

  useEffect(() => {
    const parentCategory = categories.find(
      (parentCategory: CategoryType) => parentCategory.code === category.parent
    )

    if (parentCategory) {
      setParent({
        value: parentCategory?.code,
        label: parentCategory?.name,
      })
    }

    const img: any = category.img
    if (img && typeof img === 'string') {
      setImage(img.substring(0, 4) === 'http' ? img : process.env.REACT_APP_AWS_S3_URL + img)
      setFirstStateImage(
        img.substring(0, 4) === 'http' ? img : process.env.REACT_APP_AWS_S3_URL + img
      )
    }
  }, [category])

  const edit = async () => {
    try {
      if (!name || !code) {
        Swal.fire({
          icon: 'error',
          title: 'Campos faltantes',
          text: 'Verifique se preencheu todos os campos!',
        })
      } else {
        setLoading(true)
        let imageRes = {
          success: false,
          image: '',
        }
        let res
        if (image && image !== firstStateImage) {
          if (firstStateImage) {
            await POST('/upload-images/delete-one', {
              url: firstStateImage,
            })
          }
          const formData = new FormData()
          formData.append('file', image)
          formData.append('nameImage', `category-${uuid()}.jpg`)
          formData.append('originalSize', 'true')
          imageRes = await POST('/upload-images/upload-one', formData)
          res = await PUT(`/classification/${category.id}`, {
            name: removeEspacosEmBranco(name),
            shortname: name,
            order: order ?? 0,
            code: removeEspacosEmBranco(code),
            parent: !parent || parent?.value === '0' ? null : parent.value,
            status: status,
            img: imageRes.success ? imageRes.image : '',
          })
        } else {
          if (!image) {
            res = await PUT(`/classification/${category.id}`, {
              name,
              shortname: name,
              order: order ?? 0,
              code,
              parent: !parent || parent?.value === '0' ? null : parent.value,
              status: status,
              img: 'default/no-image.png',
            })
          } else {
            res = await PUT(`/classification/${category.id}`, {
              name,
              shortname: name,
              order: order ?? 0,
              code,
              parent: !parent || parent?.value === '0' ? null : parent.value,
              status: status,
            })
          }
        }
        setLoading(false)
        if (res.success === true) {
          Swal.fire({
            icon: 'success',
            title: 'Categoria atualizada',
            text: 'Edição realizada com sucesso!',
          }).then(async () => {
            setLoading(true)
            await reloadAction()
            setLoading(false)
            onCloseAction()
          })
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Erro ao atualizar a categoria!',
          })
        }
        setLoading(false)
      }
    } catch (e: any) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e ?? 'Erro ao atualizar a categoria!',
      })
    }
  }

  const getAlterations = () => {
    const alterations = []

    if (firstStateImage != image) {
      alterations.push('"Imagem"')
    }

    if (category.name !== name) {
      alterations.push(`"${i18n.t(`smart_order.name`)}"`)
    }

    if (category.code !== code) {
      alterations.push(`"${i18n.t(`smart_order.code`)}"`)
    }

    if (category.parent != parent?.value) {
      alterations.push(`"${i18n.t(`smart_order.category_parent`)}"`)
    }

    if (category.order !== order) {
      alterations.push(`"${i18n.t(`smart_order.code`)}"`)
    }

    if (status !== category.status) {
      alterations.push('"Status"')
    }

    return alterations.join(', ')
  }

  return (
    <>
      <div onClick={() => onCloseAction()} className={styles.background}></div>
      <div className={`${styles.modalCustomCategory} modal-dialog`}>
        <div className={`${styles.modalContentCategory} modal-content`}>
          <div className='modal-header'>
            <h5 className='modal-title'>Editar Categoria</h5>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              style={{cursor: 'pointer'}}
              onClick={() => {
                onCloseAction()
              }}
            >
              <i className='fa fa-times' aria-hidden='true'></i>
            </div>
          </div>
          <div className={`modal-body text-break pt-0`}>
            <div className='row pt-4'>
              <div className={`col-md-12 ${styles.divImage}`}>
                <input
                  type='file'
                  accept='.jpg, .jpeg, .png, .gif'
                  name='file'
                  className={`${styles.inputEdit} custom-file-input opacity-0`}
                  onChange={(event) =>
                    setImage(
                      event && event.target && event.target.files ? event.target.files[0] : null
                    )
                  }
                />
                <img
                  className={styles.imgItem}
                  src={
                    image
                      ? typeof image === 'string'
                        ? image
                        : URL.createObjectURL(image)
                      : 'https://via.placeholder.com/640x640'
                  }
                  alt='Logo pluginspace'
                />
              </div>
              <div className='col-md-12'>
                <Input
                  inputAttr={{
                    type: 'default',
                  }}
                  placeholder={i18n.t(`smart_order.name`)}
                  label={i18n.t(`smart_order.name`)}
                  value={name}
                  change={(value: string) => setName(value)}
                  disabled={integration === 'totvsFashionVarejo'}
                />
              </div>
              {/* <div className='col-md-12'>
                <Input
                  inputAttr={{
                    type: 'select',
                  }}
                  search
                  label={i18n.t(`smart_order.category_parent`)}
                  options={(() => {
                    const descendants = getDescendants(categories, category.code)
                    return categories
                      .filter(
                        (cat: CategoryType) => code !== cat.code && !descendants.includes(cat.code)
                      )
                      .map((category: CategoryType) => ({
                        select: category.name,
                        value: category.code,
                      }))
                  })()}
                  value={parent}
                  change={(value: ParentType) => setParent(value)}
                  // disabled={integration === 'totvsFashionVarejo' || integration === 'totvsFood'}
                  disabled={true}
                />
              </div> */}
              <div className='col-md-6'>
                <Input
                  inputAttr={{
                    type: 'default',
                  }}
                  placeholder={i18n.t(`smart_order.order`)}
                  label={i18n.t(`smart_order.order`)}
                  value={order}
                  change={(value: string) => setOrder(value)}
                />
              </div>
              <div className='col-md-6'>
                <Input
                  inputAttr={{
                    type: 'default',
                  }}
                  placeholder={i18n.t(`smart_order.code`)}
                  label={i18n.t(`smart_order.code`)}
                  value={code}
                  change={(value: string) => {
                    if (value.match('^[a-zA-Z0-9_]*$') != null) {
                      setCode(value)
                    }
                  }}
                  disabled={integration === 'totvsFashionVarejo' || integration === 'totvsFood'}
                />
              </div>
              <div className='col-md-12'>
                <Input
                  inputAttr={{
                    type: 'checkbox',
                  }}
                  label={i18n.t(`general.active`)}
                  checked={status}
                  value={status}
                  change={(value: boolean) => {
                    setStatus(value)
                  }}
                  checkSwift
                />
              </div>
            </div>
          </div>
          <div
            className={`modal-footer mt-4 d-flex flex-column h-100 align-items-center justify-content-start`}
          >
            <button
              onClick={() => navigate(`/category/editar/${category.id}`)}
              className={styles.editButton}
            >
              {i18n.t(`smart_order.more_category`)}
            </button>
            <button
              onClick={async () => {
                if (getAlterations() && getAlterations() !== '') {
                  Swal.fire({
                    title: 'Deseja salvar as alterações?',
                    showDenyButton: true,
                    showCancelButton: false,
                    confirmButtonText: i18n.t(`buttons.save`),
                    denyButtonText: i18n.t(`smart_order.cancel`),
                    confirmButtonColor: '#6E7586',
                    text: `Foram feitas alterações na categoria no(s) campo(s) ${getAlterations()}.`,
                  }).then(async (result) => {
                    if (result.isConfirmed) {
                      await edit()
                    }
                  })
                } else onCloseAction()
              }}
              className={styles.saveButton}
            >
              {i18n.t(`buttons.save`)}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export {EditCategoriaModal}
